// Aggiornamenti della web app senza reinstallarla.
//
// Su iPhone un'app aggiunta alla Home non ha la barra di Safari, quindi niente
// "ricarica": se il service worker non cerca da solo una versione nuova, l'utente
// resta sulla vecchia finche' non la rimuove e la riaggiunge. Qui:
//   - si controlla se c'e' una versione nuova all'avvio, ogni volta che l'app torna
//     in primo piano e ogni 30 minuti;
//   - la versione nuova si applica da sola quando non disturba: subito se l'app si
//     e' appena aperta, oppure quando va in background; mai mentre un video e' in
//     riproduzione (si perderebbe la riproduzione, AirPlay compreso);
//   - nel frattempo un banner permette di aggiornare subito.

import { registerSW } from 'virtual:pwa-register';
import { isNativeApp } from './nativeShell.js';

const CHECK_INTERVAL_MS = 30 * 60 * 1000;
// entro questo tempo dall'apertura un ricaricamento passa per "caricamento lento"
const STARTUP_GRACE_MS = 6000;

const startedAt = Date.now();
const listeners = new Set();
let updateAvailable = false;
let applySW = null;
let applying = false;

const notify = () => listeners.forEach((l) => l(updateAvailable));

function isPlaybackActive() {
    return [...document.querySelectorAll('video')].some((v) => !v.paused && !v.ended);
}

export function applyUpdate() {
    if (!applySW || applying) return;
    applying = true;
    // Attiva il nuovo service worker e ricarica la pagina. La ricarica la facciamo
    // noi su controllerchange: quella di workbox-window, con clientsClaim attivo,
    // in prova non partiva (il nuovo SW si attivava ma la pagina restava vecchia).
    let reloaded = false;
    const reload = () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', reload, { once: true });
    setTimeout(reload, 4000); // rete di sicurezza
    applySW(false);
}

function applyIfQuiet(reason) {
    if (!updateAvailable || isPlaybackActive()) return;
    if (reason === 'startup' && Date.now() - startedAt > STARTUP_GRACE_MS) return;
    applyUpdate();
}

export function subscribeUpdate(listener) {
    listeners.add(listener);
    listener(updateAvailable);
    return () => listeners.delete(listener);
}

export function initPwaUpdates() {
    if (!('serviceWorker' in navigator)) return;
    // Nell'app (Capacitor) niente service worker: la WebView carica sempre il sito
    // dalla rete, quindi è già all'ultima versione senza banner né ricariche.
    if (isNativeApp) return;

    applySW = registerSW({
        immediate: true,
        onNeedRefresh() {
            updateAvailable = true;
            notify();
            applyIfQuiet('startup');
        },
        onRegisteredSW(_url, registration) {
            if (!registration) return;
            const check = () => {
                // offline o server giu': si riprova al prossimo giro
                if (navigator.onLine) registration.update().catch(() => {});
            };
            setInterval(check, CHECK_INTERVAL_MS);
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'visible') check();
                else applyIfQuiet('background');
            });
        }
    });
}
